import { useMemo, useState } from 'react';
import { Text, Spinner, Badge, Button, makeStyles, tokens } from '@fluentui/react-components';
import { MapRegular, ArrowUploadRegular, AddRegular, DeleteRegular } from '@fluentui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { fetchModules, fetchProjects, type ModuleStatus } from '../api/modules.api';
import { fetchCrops } from '../api/terrain.api';
import { useT } from '../i18n';
import { useRanchoStore } from '../store/rancho.store';
import { fetchLotesForMap } from '../api/dashboard.api';
import { patchModuleBoundary, deleteModule } from '../api/terrain.api';
import { parseKmlPolygon } from '../utils/kml';
import { polygonContainsPoint } from '../components/shared/MapView';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { parseLoteZoneId } from '../utils/lote-zone-options';
import { useLoteZoneOptions } from '../hooks/useLoteZoneOptions';
import { usePermission } from '../hooks/usePermission';
import { useProjectStore } from '../store/project.store';
import { FarmModuleFormDialog } from './MapPage';
import { PageTitle } from '../components/shared/PageTitle';

/**
 * F7 — Grid de módulos del portal inversionista. Reusa la API real de F1
 * (`GET /api/modules`, PR #90) — cada tarjeta es un `FarmModule` real, no un
 * placeholder. El mapa con polígonos vive en `MapPage.tsx` (edición de
 * geocercas); esta vista es de solo lectura y no lo duplica.
 * Cada tarjeta navega al detalle (`InvestorModuleDetailPage`, Fase B) con
 * fenología, sensores, video de dron y anomalías del módulo.
 */

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  grid:    { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalM },
  card: {
    width: '220px',
    padding: '16px 18px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    borderLeft: '4px solid transparent',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    cursor: 'pointer',
    ':hover': { boxShadow: tokens.shadow4 },
  },
  code:    { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  crop:    { color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase300 },
  meta:    { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  reason:  { fontSize: tokens.fontSizeBase200, marginTop: '4px' },
  legend:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', alignItems: 'center' },
  chip:    { cursor: 'pointer', border: 'none', background: 'none', padding: 0, opacity: 0.55 },
  chipOn:  { opacity: 1 },
  loteTitle: { fontWeight: tokens.fontWeightSemibold, marginTop: tokens.spacingVerticalS },
  childCard: { marginLeft: '20px', width: '200px' },
});

const STATUS_BORDER: Record<ModuleStatus, string> = {
  PREPARACION: tokens.colorNeutralStroke2,
  NORMAL:      tokens.colorPaletteGreenBorder2,
  ATENCION:    tokens.colorPaletteYellowBorder2,
  CRITICO:     tokens.colorPaletteRedBorder2,
  COSECHANDO:  tokens.colorPaletteBlueBorderActive,
};

const STATUS_BADGE: Record<ModuleStatus, 'subtle' | 'success' | 'warning' | 'danger' | 'informative'> = {
  PREPARACION: 'subtle',
  NORMAL:      'success',
  ATENCION:    'warning',
  CRITICO:     'danger',
  COSECHANDO:  'informative',
};

function daysSince(dateStr?: string): number | null {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.max(0, Math.floor(diff / 86_400_000));
}

export function InvestorModulesPage() {
  const s = useStyles();
  const t = useT();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();

  const qc = useQueryClient();
  const canWrite = usePermission('lotes:write');
  const [kmlMessage, setKmlMessage] = useState('');
  // Alta de zona: lote destino y, para una subzona, la zona padre.
  const [createFor, setCreateFor] = useState<{ loteId: string; parentId?: string } | null>(null);
  const activeProjectId = useProjectStore(st => st.activeProjectId);
  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);
  const { data: projects } = useQuery({ queryKey: ['investor-projects', activeRanchoId], queryFn: () => fetchProjects(activeRanchoId) });
  const { data: modules, isLoading } = useQuery({ queryKey: ['investor-modules', activeRanchoId], queryFn: () => fetchModules({ ranchoId: activeRanchoId }) });

  const { data: lotes = [] } = useQuery({ queryKey: ['lotes-map', activeRanchoId], queryFn: () => fetchLotesForMap(activeRanchoId) });
  const { data: crops = [] } = useQuery({ queryKey: ['crops-catalog'], queryFn: fetchCrops });

  const [statusFilter, setStatusFilter] = useState<Set<ModuleStatus>>(new Set());
  // Un solo selector de árbol (lote, zona o subzona). Con zona elegida se ven sus hijos finales.
  const [scope, setScope] = useState('');
  const scopeSel = parseLoteZoneId(scope, modules ?? []);
  const loteFilter = scopeSel?.loteId ?? '';
  const zoneFilter = scopeSel?.moduleId ?? '';
  const toggleStatus = (st: ModuleStatus) => setStatusFilter(prev => {
    const next = new Set(prev);
    if (next.has(st)) next.delete(st); else next.add(st);
    return next;
  });

  const project = projects?.[0];
  const loteName = (id: string) => lotes.find(l => l.loteId === id)?.name ?? id;
  const lotesWithZones = useMemo(
    // Todos los lotes del rancho, con o sin zonas: un lote vacío también recibe zonas.
    () => [...new Set([...lotes.map(l => l.loteId), ...(modules ?? []).map(m => m.loteId)])].sort((a, b) => loteName(a).localeCompare(loteName(b), undefined, { numeric: true })),
    [modules, lotes], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Árbol: lote → zona de primer nivel → sus zonas hijas. Una hija cuyo padre
  // quedó fuera del filtro se muestra como zona suelta.
  const tree = useMemo(() => {
    const byCode = (a: { moduleCode: string }, b: { moduleCode: string }) => a.moduleCode.localeCompare(b.moduleCode, undefined, { numeric: true });
    const shown = (modules ?? []).filter(m => (!loteFilter || m.loteId === loteFilter) && (statusFilter.size === 0 || statusFilter.has(m.status)));
    const ids = new Set(shown.map(m => m.moduleId));
    return lotesWithZones.map(loteId => {
      const inLote = shown.filter(m => m.loteId === loteId);
      if (zoneFilter) {
        // Zona elegida: sus hijos finales son las tarjetas; sin hijos, la zona misma.
        const zone = inLote.find(m => m.moduleId === zoneFilter);
        const kids = inLote.filter(m => m.parentModuleId === zoneFilter).sort(byCode);
        const picked = kids.length ? kids : (zone ? [zone] : []);
        return { loteId, groups: picked.map(root => ({ root, children: [] as typeof inLote })) };
      }
      const roots = inLote.filter(m => !m.parentModuleId || !ids.has(m.parentModuleId)).sort(byCode);
      return {
        loteId,
        groups: roots.map(root => ({ root, children: inLote.filter(m => m.parentModuleId === root.moduleId).sort(byCode) })),
      };
    }).filter(l => l.groups.length > 0);
  }, [modules, lotesWithZones, loteFilter, zoneFilter, statusFilter]);

  // KML → geocerca de la zona. Se exige que quede dentro de su zona padre (o del lote).
  const handleKml = async (m: { moduleId: string; loteId: string; parentModuleId?: string }, file: File) => {
    setKmlMessage('');
    const boundary = parseKmlPolygon(await file.text());
    if (!boundary) { setKmlMessage(t.investorModules.kmlInvalid); return; }
    const parent = m.parentModuleId ? modules?.find(x => x.moduleId === m.parentModuleId) : undefined;
    const container = (parent as { boundary?: typeof boundary } | undefined)?.boundary
      ?? lotes.find(l => l.loteId === m.loteId)?.boundary;
    const cRing = container?.coordinates?.[0]?.map(([lng, lat]) => [lat, lng] as [number, number]);
    if (cRing?.length && !boundary.coordinates[0].every(([lng, lat]) => polygonContainsPoint([lat, lng], cRing))) {
      setKmlMessage(t.investorModules.kmlOutside);
      return;
    }
    try {
      await patchModuleBoundary(m.moduleId, boundary);
      qc.invalidateQueries({ queryKey: ['investor-modules'] });
      qc.invalidateQueries({ queryKey: ['modules-map-at-date'] });
      setKmlMessage(t.investorModules.kmlSaved);
    } catch {
      setKmlMessage(t.investorModules.kmlError);
    }
  };

  const handleDeleteZone = async (m: { moduleId: string; moduleCode: string; name?: string }) => {
    if (!window.confirm(`${t.terrain.deleteModuleConfirm} ${m.moduleCode}${m.name ? ` — ${m.name}` : ''}?`)) return;
    try {
      await deleteModule(m.moduleId);
      qc.invalidateQueries({ queryKey: ['investor-modules'] });
      qc.invalidateQueries({ queryKey: ['modules-map-at-date'] });
      setKmlMessage('');
    } catch (e: any) {
      setKmlMessage(e?.response?.data?.error === 'MODULE_HAS_CHILDREN' ? t.terrain.deleteModuleHasChildren : t.terrain.deleteModuleError);
    }
  };

  // Un lote con cultivo se muestra con su cultivo y ciclo, aunque tenga zonas.
  const loteCropLabel = (id: string): string | null => {
    const lo = lotes.find(l => l.loteId === id) as { cropId?: string; cropCycleLabel?: string } | undefined;
    if (!lo?.cropId) return null;
    const name = crops.find(c => c.cropId === lo.cropId)?.name ?? lo.cropId;
    return lo.cropCycleLabel ? `${name} · ${lo.cropCycleLabel}` : name;
  };

  const renderCard = (m: (typeof tree)[number]['groups'][number]['root'], isChild: boolean) => {
    const days = daysSince(m.plantingDate);
    return (
      <div
        key={m.moduleId}
        className={`${s.card} ${isChild ? s.childCard : ''}`}
        style={{ borderLeftColor: STATUS_BORDER[m.status] }}
        onClick={() => navigate(`/${tenantId}/fincas/modulo/${m.moduleId}`)}
      >
        <span className={s.code}>{isChild ? '↳ ' : ''}{m.moduleCode}</span>
        <span className={s.crop}>
          {(m.cropId ? crops.find(c => c.cropId === m.cropId)?.name : undefined) ?? m.cropName ?? t.investorModules.noCrop}
          {m.cropCycleLabel && ` · ${m.cropCycleLabel}`}
        </span>
        <Badge appearance="tint" color={STATUS_BADGE[m.status]} size="small">
          {t.investorModules[`status${m.status}` as keyof typeof t.investorModules]}
        </Badge>
        {m.areaHa != null && <span className={s.meta}>{m.areaHa.toFixed(1)} ha</span>}
        {days != null && <span className={s.meta}>{t.investorModules.daysSincePlanting}: {days}</span>}
        {m.statusReason && <span className={s.reason}>{m.statusReason}</span>}
        <Button
          size="small" appearance="subtle" icon={<MapRegular />} style={{ alignSelf: 'flex-start', marginTop: '4px' }}
          onClick={e => { e.stopPropagation(); navigate(`/${tenantId}/mapa?zoneId=${encodeURIComponent(m.moduleId)}`); }}
        >
          {t.investorModules.viewOnMap}
        </Button>
        {canWrite && !isChild && (
          <Button
            size="small" appearance="subtle" icon={<AddRegular />} style={{ alignSelf: 'flex-start' }}
            onClick={e => { e.stopPropagation(); setCreateFor({ loteId: m.loteId, parentId: m.moduleId }); }}
          >
            {t.terrain.createModuleBtn}
          </Button>
        )}
        {canWrite && (
          <>
            <input
              id={`kml-${m.moduleId}`} type="file" accept=".kml,application/vnd.google-earth.kml+xml" hidden
              onClick={e => e.stopPropagation()}
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void handleKml(m, f); }}
            />
            <Button
              size="small" appearance="subtle" icon={<ArrowUploadRegular />} style={{ alignSelf: 'flex-start' }}
              onClick={e => { e.stopPropagation(); document.getElementById(`kml-${m.moduleId}`)?.click(); }}
            >
              KML
            </Button>
          </>
        )}
        {canWrite && (
          <Button
            size="small" appearance="subtle" icon={<DeleteRegular />} style={{ alignSelf: 'flex-start' }}
            onClick={e => { e.stopPropagation(); void handleDeleteZone(m); }}
          >
            {t.terrain.deleteModuleBtn}
          </Button>
        )}
      </div>
    );
  };

  const scopeOptions = useLoteZoneOptions(lotes, modules ?? [], { campoOnly: true });

  return (
    <div className={s.root}>
      <PageTitle>{t.investorModules.title}</PageTitle>
      <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
        {project ? `${project.name}${project.season ? ` — ${project.season}` : ''}` : t.investorModules.subtitle}
      </Text>

      <div className={s.legend}>
        {(Object.keys(STATUS_BADGE) as ModuleStatus[]).map(st => (
          <button
            key={st} type="button" aria-pressed={statusFilter.has(st)}
            className={`${s.chip} ${statusFilter.has(st) ? s.chipOn : ''}`}
            onClick={() => toggleStatus(st)}
          >
            <Badge appearance={statusFilter.has(st) ? 'filled' : 'tint'} color={STATUS_BADGE[st]}>
              {t.investorModules[`status${st}` as keyof typeof t.investorModules]}
            </Badge>
          </button>
        ))}
        <div style={{ minWidth: '280px' }}>
          <SearchableSelect
            label={t.investorModules.loteFilter}
            placeholder={t.common.searchLotePlaceholder}
            options={scopeOptions}
            value={scope}
            onChange={setScope}
            noResultsText={t.common.searchNoResults}
          />
        </div>
      </div>

      {kmlMessage && <Text size={200} role="status">{kmlMessage}</Text>}
      {isLoading && <Spinner size="small" label={t.investorModules.loading} />}
      {!isLoading && tree.length === 0 && <Text size={200}>{t.investorModules.noModules}</Text>}

      {tree.map(({ loteId, groups }) => (
        <div key={loteId} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalS }}>
            <div>
              <Text className={s.loteTitle}>{loteName(loteId)}</Text>
              {loteCropLabel(loteId) && <div className={s.crop}>{loteCropLabel(loteId)}</div>}
            </div>
            {canWrite && (
              <Button size="small" appearance="subtle" icon={<AddRegular />} onClick={() => setCreateFor({ loteId })}>
                {t.terrain.createModuleBtn}
              </Button>
            )}
          </div>
          {groups.map(({ root, children }) => (
            <div key={root.moduleId} className={s.grid}>
              {[root, ...children].map(m => renderCard(m, m.moduleId !== root.moduleId))}
            </div>
          ))}
        </div>
      ))}

      {createFor && (
        <FarmModuleFormDialog
          open
          loteId={createFor.loteId}
          defaultParentId={createFor.parentId}
          activeProjectId={activeProjectId}
          loteName={loteName(createFor.loteId)}
          loteModules={(modules ?? []).filter(m => m.loteId === createFor.loteId)}
          onClose={() => setCreateFor(null)}
        />
      )}
    </div>
  );
}
